/**
 * Voice/Video/Screenshare (Paket feat/voice, docs/API.md 4.6) – gemeinsame Konstanten, Eingaben und Hilfsfunktionen
 * für API (LiveKit-Token, VoiceStates) und Web (livekit-client).
 */
import type { VoiceJoinDto, VoiceStateDto } from './dto';
import { SCREENSHARE_PRESETS, SCREENSHARE_PRESET_SPECS, type ScreensharePreset } from './limits';
import type { Snowflake } from './snowflake';

/** LiveKit-Raumname eines Sprachkanals: `ch_<channelId>`. */
export const VOICE_ROOM_PREFIX = 'ch_';

export function voiceRoomName(channelId: Snowflake): string {
  return `${VOICE_ROOM_PREFIX}${channelId}`;
}

/** Kanal-ID aus einem Raumnamen; null, wenn der Name nicht dem Schema entspricht. */
export function channelIdFromRoom(room: string | null | undefined): Snowflake | null {
  if (!room || !room.startsWith(VOICE_ROOM_PREFIX)) return null;
  const id = room.slice(VOICE_ROOM_PREFIX.length);
  return /^\d{1,20}$/.test(id) ? id : null;
}

/**
 * Lebensdauer des LiveKit-Tokens (Sekunden) – Vertrag 4.6: 6 h.
 * @deprecated Ersetzt durch {@link VOICE_CONNECT_TOKEN_TTL_SECONDS} (Sicherheitsreview H1, Abweichung vom Vertrag);
 * bleibt nur für Kompatibilität bestehen.
 */
export const VOICE_TOKEN_TTL_SECONDS = 6 * 60 * 60;

/**
 * Gültigkeit des Join-Tokens (Sekunden) – nur für den Verbindungsaufbau (Sicherheitsreview H1, ersetzt die 6 h aus
 * Vertrag 4.6). livekit-server ≥ 1.x schickt verbundenen Clients direkt nach dem Beitritt und danach alle 5 min ein
 * frisches Token (10 min gültig, aktuelle Rechte, Attribute erhalten; geprüft mit 1.9.1), das livekit-client für
 * Reconnects nutzt. Ein abgegriffenes Join-Token taugt so nur kurz; ein neuer Beitritt holt immer `POST …/join`.
 */
export const VOICE_CONNECT_TOKEN_TTL_SECONDS = 120;

/** Kanal-Bitrate, wenn der Kanal keine eigene hat (bit/s). Wird zusätzlich auf maxAudioBitrate gedeckelt. */
export const DEFAULT_VOICE_BITRATE = 96_000;
/** Untergrenze für Kanal-Bitraten (bit/s). */
export const MIN_VOICE_BITRATE = 8_000;

/** Standard-Qualität beim Bildschirmteilen (wird auf das Admin-Limit gedeckelt). */
export const DEFAULT_SCREENSHARE_PRESET: ScreensharePreset = '1080p60';

/** Metadaten eines LiveKit-Teilnehmers (`participant.metadata`, JSON). */
export interface VoiceParticipantMetadata {
  userId: Snowflake;
  username: string;
  /** Anzeigename (Fallback username). Additiv zum Vertrag. */
  displayName: string;
  avatarUrl: string | null;
}

/** LiveKit-Teilnehmer-Attribut mit der Sitzungs-ID des VoiceStates (Webhook-Zuordnung). */
export const VOICE_SESSION_ATTRIBUTE = 'lf_session';

/** Antwort von `POST /voice/channels/:channelId/join` – `VoiceJoinDto` plus additive Felder. */
export interface VoiceJoinResponseDto extends VoiceJoinDto {
  /** Der angelegte/verschobene Sprachstatus. */
  voiceState: VoiceStateDto;
  channelId: Snowflake;
  serverId: Snowflake | null;
  /** Wirksame Teilnehmergrenze (0 = unbegrenzt). */
  userLimit: number;
  /** Admin-Limit (bit/s), zur Anzeige. */
  maxAudioBitrate: number;
}

/** Body von `POST /voice/channels/:channelId/join`. */
export interface VoiceJoinInput {
  selfMute?: boolean;
  selfDeaf?: boolean;
}

/** Body von `PATCH /voice/state` bzw. Socket `VOICE_STATE_SET`. */
export interface VoiceStatePatchInput {
  selfMute?: boolean;
  selfDeaf?: boolean;
  video?: boolean;
  streaming?: boolean;
}

/** Body von `POST /voice/channels/:channelId/members/:userId/move`. */
export interface VoiceMoveInput {
  channelId: Snowflake;
}

/** Antwort von `GET /voice/ice-servers`. */
export interface IceServersDto {
  iceServers: VoiceJoinDto['iceServers'];
}

/** Alle Bildschirm-Presets bis einschließlich `max` (aufsteigend). */
export function allowedScreensharePresets(max: ScreensharePreset | string): ScreensharePreset[] {
  const idx = SCREENSHARE_PRESETS.indexOf(max as ScreensharePreset);
  if (idx < 0) return [SCREENSHARE_PRESETS[0]];
  return SCREENSHARE_PRESETS.slice(0, idx + 1);
}

/** Gewünschtes Preset auf das Limit deckeln (unbekannt → Standard, ebenfalls gedeckelt). */
export function clampScreensharePreset(wanted: string | null | undefined, max: ScreensharePreset | string): ScreensharePreset {
  const allowed = allowedScreensharePresets(max);
  const top = allowed[allowed.length - 1] as ScreensharePreset;
  const want = (SCREENSHARE_PRESETS as readonly string[]).includes(wanted ?? '') ? (wanted as ScreensharePreset) : DEFAULT_SCREENSHARE_PRESET;
  return allowed.includes(want) ? want : top;
}

/** Encoding-Parameter eines Presets (für livekit-client `screenShareEncoding`/`resolution`). */
export function screensharePresetSpec(preset: ScreensharePreset | string) {
  return SCREENSHARE_PRESET_SPECS[(SCREENSHARE_PRESETS as readonly string[]).includes(preset) ? (preset as ScreensharePreset) : DEFAULT_SCREENSHARE_PRESET];
}

/** Wirksame Audio-Bitrate: `min(channel.bitrate ?? DEFAULT, maxAudioBitrate)`, mindestens MIN_VOICE_BITRATE. */
export function effectiveVoiceBitrate(channelBitrate: number | null | undefined, maxAudioBitrate: number): number {
  const wanted = channelBitrate && channelBitrate > 0 ? channelBitrate : DEFAULT_VOICE_BITRATE;
  const capped = maxAudioBitrate > 0 ? Math.min(wanted, maxAudioBitrate) : wanted;
  return Math.max(MIN_VOICE_BITRATE, capped);
}

/** Wirksame Teilnehmergrenze aus Kanal-`userLimit` und Plattform-`maxVoiceUsers` (0 = unbegrenzt). */
export function effectiveVoiceUserLimit(channelUserLimit: number | null | undefined, maxVoiceUsers: number): number {
  const limits = [channelUserLimit ?? 0, maxVoiceUsers].filter((n) => n > 0);
  return limits.length === 0 ? 0 : Math.min(...limits);
}
